/**
 * @file historyLock.ts
 * @brief B39 (28-10-2026): items older than this user's history window.
 *
 * @details
 * The backend hides older meetings, notes and interpretation sessions from
 * its lists and answers 403 `HISTORY_LOCKED` on their detail (Free keeps 30
 * days; the viewer's plan decides). DES 28-10: keep that rule for launch and
 * only fix how it shows. Lists that still reach an old item (to-dos, Ask
 * sources, Inbox) mark it with a lock using `history.from` from
 * GET /billing/subscriptions; the detail shows the server's sentence, never
 * "Couldn't load".
 *
 * B53 (04-11-2026): someone else's meeting / note follows its OWNER's plan
 * window, decided by the server. The client no longer locks with
 * `history.from` except as the fallback for an older backend (my to-dos
 * without `historyLocked`). A 403 with `historyWindow: "owner"` has no
 * "Pick a plan" (upgrading the viewer cannot open it).
 *
 * Pure (relative imports only) so node:test can load it.
 */

/** True when `err` is the 403 HISTORY_LOCKED refusal (ApiClientError or a body). */
export function isHistoryLockedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: unknown; status?: unknown };
  return e.code === "HISTORY_LOCKED";
}

/** The server's sentence of a HISTORY_LOCKED error, or null. */
export function historyLockedServerMessage(err: unknown): string | null {
  if (!isHistoryLockedError(err)) return null;
  const m = (err as { message?: unknown }).message;
  return typeof m === "string" && m.trim() ? m.trim() : null;
}

/**
 * True when an item dated `itemDate` is before the window start `from`
 * (same comparison as the backend's isHistoryLocked). No date or no window
 * = not locked.
 */
export function isPastHistory(
  itemDate: string | Date | null | undefined,
  from: string | null | undefined,
): boolean {
  if (!itemDate || !from) return false;
  const t = new Date(itemDate).getTime();
  const f = Date.parse(from);
  return Number.isFinite(t) && Number.isFinite(f) && t < f;
}

/**
 * B53: which plan's window refused a HISTORY_LOCKED error: "owner" (someone
 * else's meeting / note, owner's plan), "mine", or null (not this error, or
 * an older backend without the field = treat as "mine"). Reads the field on
 * the error itself or on the response body (ApiClientError.raw is the axios
 * error, body at raw.response.data).
 */
export function historyLockedWindow(err: unknown): "mine" | "owner" | null {
  if (!isHistoryLockedError(err)) return null;
  const e = err as { historyWindow?: unknown; raw?: { historyWindow?: unknown; response?: { data?: { historyWindow?: unknown } } } };
  const w = e.historyWindow ?? e.raw?.response?.data?.historyWindow ?? e.raw?.historyWindow;
  return w === "owner" ? "owner" : "mine";
}

/** B53: the locked view for someone else's meeting / note: server sentence, no button. */
export function ownerHistoryLockedView(serverMessage: string | null): { title: string; message: string } {
  return {
    title: "Older than the owner's plan keeps",
    message: serverMessage || "The owner's plan no longer keeps this, so it is not available.",
  };
}

/**
 * B53: the lock on a My to-dos row. The server's per-item `historyLocked`
 * (owner's window for someone else's meeting, mine for my own) wins; an
 * older backend without it falls back to the B39 date check on my window.
 */
export function todoHistoryLocked(
  item: { historyLocked?: unknown; meetingDate?: string | null },
  from: string | null | undefined,
): boolean {
  if (typeof item.historyLocked === "boolean") return item.historyLocked;
  return isPastHistory(item.meetingDate, from);
}
